import { LoginService } from '../../services/login.service';
import { switchMap, tap } from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  TemplateRef,
} from '@angular/core';
import * as moment from 'moment';
import { ConfigService } from '../../services/config.service';
import { SurveyFilter } from './survey.filter.model';
import { ProcessParticipationRespondent } from '../../models/process-models';
import { ProcessParticipationService } from '../../services/process.participation.service';
import { User } from '../../models/identity';
import { AppraiseeProvider } from '../../common/providers/appraisee.provider';
import { LanguageService } from '../../services/language.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { ProcessParticipationAppraisee } from '../../models/process-models/process-participation-appraisee.model';

@Component({
  selector: 'app-survey-list',
  templateUrl: './survey-list.component.html',
  styleUrls: ['./survey-list.component.scss'],
})
export class SurveyListComponent implements OnInit {
  currentUser: User;
  respondents: Array<ProcessParticipationRespondent>;
  surveyFilter: any = SurveyFilter;
  currentFilter: SurveyFilter;
  surveyShortTimeLeft: number;
  surveyMiddleTimeLeft: number;
  isSelfassessmentTab: boolean;
  modalRef: BsModalRef;

  private decimailScsle = 10;
  constructor(
    private processParticipationService: ProcessParticipationService,
    private configService: ConfigService,
    private router: Router,
    private readonly appraiseeProvider: AppraiseeProvider,
    private readonly route: ActivatedRoute,
    private readonly languageService: LanguageService,
    private readonly ref: ChangeDetectorRef,
    private readonly bsModalService: BsModalService,
    private readonly translateService: TranslateService,
    private readonly loginService: LoginService
  ) {}

  ngOnInit() {
    this.getSurveysList();
    this.changeLanguage();
    this.currentUser = this.loginService.getUser;
    this.surveyShortTimeLeft = this.configService.configData[
      'surveyShortTimeLeft'
    ];
    this.surveyMiddleTimeLeft = this.configService.configData[
      'surveyMiddleTimeLeft'
    ];
  }

  getLocalizedDate(date): string {
    return moment(date).format('DD MMMM YYYY');
  }

  setTimeLeft(survey: ProcessParticipationRespondent) {
    const endDate = moment(survey.endDate).add(1, 'day');
    if (endDate.isAfter(moment(new Date()), 'day')) {
      this.setAfterTimeLeft(survey, endDate);
    } else {
      this.setNullTimeLeft(survey);
    }
  }

  filterSurveys() {
    return this.isSelfassessmentTab
      ? this.respondents.filter(
          (s) =>
            s.appraisee !== null &&
            s.appraisee.personId === this.currentUser.personId
        )
      : this.respondents.filter(
          (s) =>
            s.appraisee === null ||
            s.appraisee.personId !== this.currentUser.personId
        );
  }

  navigateToSurvey(processInfo: ProcessParticipationRespondent) {
    if (!this.isSelfassessmentTab) {
      this.router.navigate([
        '/user/survey-list/survey',
        { id: processInfo.processParticipationId },
      ]);
    } else {
      this.router.navigate([
        '/user/my-page/self-assessment/survey',
        {
          id: processInfo.processParticipationId,
          currentFilter: SurveyFilter.ByAppraisee,
        },
      ]);
    }
  }

  getIsAppraisee(appraisee: ProcessParticipationAppraisee): boolean {
    return !!appraisee;
  }

  getAppraiseeName(appraisee: ProcessParticipationAppraisee): string {
    return this.appraiseeProvider.getAppraiseeName(appraisee);
  }

  openSkipSurveyModal(
    skipSurveyModal: TemplateRef<any>,
    respondent: ProcessParticipationRespondent
  ) {
    this.modalRef = this.bsModalService.show(skipSurveyModal);
    this.modalRef.content = respondent;
  }

  hideModal() {
    this.modalRef.content = null;
    this.modalRef.hide();
  }

  skipSurvey() {
    const processParticipationId = this.modalRef.content.processParticipationId;
    this.processParticipationService
      .skipSurvey(processParticipationId)
      .subscribe(() => {
        this.hideModal();
        const index = this.respondents.findIndex(
          (r) => r.processParticipationId === processParticipationId
        );
        if (index === -1) {
          return;
        }
        this.respondents.splice(index, 1);
      });
  }

  getSkipSurveyMessage() {
    let format = '';
    this.translateService
      .get('lang.survey.skipMessageFormat')
      .subscribe((x) => (format = x as string));
    const respondent = this.modalRef.content as ProcessParticipationRespondent;
    if (this.modalRef.content && !respondent) {
      throw new Error('Respondent property is null');
    }
    if (!this.modalRef.content) {
      return '';
    }
    const appraiseeName =
      respondent.appraisee != null
        ? respondent.appraisee.name
        : respondent.process.name;
    return format.replace('[appraiseeName]', appraiseeName);
  }

  private getSurveysList() {
    this.route.paramMap
      .pipe(
        tap((params) => {
          this.isSelfassessmentTab =
            parseInt(params.get('currentFilter'), this.decimailScsle) ===
            SurveyFilter.ByAppraisee
              ? true
              : false;
        }),
        switchMap(() =>
          this.processParticipationService.getAll(this.isSelfassessmentTab)
        )
      )
      .subscribe((results) => {
        results.forEach((survey) => this.setTimeLeft(survey));
        this.respondents = results;
        this.sortSurvey(this.respondents);
        this.ref.markForCheck();
      });
  }

  private setAfterTimeLeft(
    survey: ProcessParticipationRespondent,
    endDate: moment.Moment
  ) {
    const duration = moment.duration(endDate.diff(moment(Date.now())));
    survey.timeLeft = {
      days: +duration.asDays().toFixed(0),
      hours: duration.get('hours'),
      minutes: duration.get('minutes'),
    };
  }

  private setNullTimeLeft(survey: ProcessParticipationRespondent) {
    survey.timeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
    };
  }

  private sortSurvey(respondents: Array<ProcessParticipationRespondent>) {
    respondents.sort((a, b) => {
      return moment
        .duration(moment(a.endDate).diff(moment(b.endDate)))
        .asDays();
    });
  }

  private changeLanguage() {
    this.languageService.languageChanged$.subscribe(() => {
      this.getSurveysList();
    });
  }
}
